import { useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard } from '../api/guard'
import { isUuid, shortId } from './ids'
import './screens.css'

interface EndUser {
  id: string
  workspace_id: string
  kind: 'member' | 'external'
  external_id: string | null
  member_user_id: string | null
  disabled_at: string | null
  created_at: string | null
  updated_at: string | null
}
interface EndUsersResponse {
  end_users: EndUser[]
  limit: number
  offset: number
  has_more: boolean
}

const PAGE_SIZE = 50

function when(v: string | null): string {
  return v ? new Date(v).toLocaleString() : '—'
}

export function EndUsersView() {
  const [workspace, setWorkspace] = useState('')
  const [kind, setKind] = useState<'' | 'member' | 'external'>('')
  const [offset, setOffset] = useState(0)

  const ws = workspace.trim()
  const wsInvalid = ws !== '' && !isUuid(ws)
  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) })
  if (ws && !wsInvalid) params.set('workspace_id', ws)
  if (kind) params.set('kind', kind)

  const query = useQuery<EndUsersResponse>({
    queryKey: ['admin-end-users', wsInvalid ? '' : ws, kind, offset],
    queryFn: () => apiFetch<EndUsersResponse>(`/admin/end-users?${params.toString()}`),
    placeholderData: keepPreviousData,
  })
  useForbiddenGuard(query.error)

  const rows = query.data?.end_users ?? []
  const hasMore = query.data?.has_more ?? false

  return (
    <section className="scr" aria-labelledby="eu-heading">
      <div className="screen-head">
        <h2 id="eu-heading">End Users</h2>
      </div>

      <div className="scr-filters">
        <input
          type="text"
          aria-label="Filter by workspace ID"
          placeholder="Workspace ID (UUID)"
          value={workspace}
          onChange={(e) => {
            setWorkspace(e.target.value)
            setOffset(0)
          }}
        />
        <select
          aria-label="Filter by kind"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as '' | 'member' | 'external')
            setOffset(0)
          }}
        >
          <option value="">All kinds</option>
          <option value="member">member</option>
          <option value="external">external</option>
        </select>
      </div>
      {wsInvalid && <p className="scr-err">Workspace ID must be a UUID; filter not applied.</p>}

      <p className="scr-note">Read-only, across all workspaces, newest first.</p>

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load end users.
        </p>
      )}

      <table className="scr-table">
        <thead>
          <tr>
            <th>End user</th>
            <th>Workspace</th>
            <th>Kind</th>
            <th>Identity</th>
            <th>Status</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} className={u.disabled_at ? 'scr-row--muted' : undefined}>
              <td className="scr-mono" title={u.id}>{shortId(u.id)}</td>
              <td className="scr-mono" title={u.workspace_id}>{shortId(u.workspace_id)}</td>
              <td>{u.kind}</td>
              <td>
                {u.kind === 'external' ? (
                  u.external_id ?? '—'
                ) : (
                  <span className="scr-mono" title={u.member_user_id ?? ''}>
                    {u.member_user_id ? shortId(u.member_user_id) : '—'}
                  </span>
                )}
              </td>
              <td>
                {u.disabled_at ? (
                  <span className="scr-badge scr-badge--error" title={when(u.disabled_at)}>
                    disabled
                  </span>
                ) : (
                  <span className="scr-badge scr-badge--success">enabled</span>
                )}
              </td>
              <td>{when(u.created_at)}</td>
            </tr>
          ))}
          {!query.isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={6} className="ws-empty">
                No end users.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="ws-pager">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
        >
          Previous
        </button>
        <span className="ws-count">Page {Math.floor(offset / PAGE_SIZE) + 1}</span>
        <button type="button" disabled={!hasMore} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
          Next
        </button>
      </div>
    </section>
  )
}
